import { MAX_COLUMNS, type Band, type Motivation } from '@hmawiw/shared';
import { create } from 'zustand';

const COLUMN_KEY = 'hmawiw-column';
const INTRO_KEY = 'hmawiw-intro-seen';
const HISTORY_MAX = 8;

export interface QuickAddPrefill {
  title?: string;
  notes?: string;
  /** The column and band whose + was pressed. */
  motivation?: Motivation;
  band?: Band;
  /** Sort later: from the widget or a share, the card waits in the inbox. */
  inbox?: boolean;
}

export interface ToastAction {
  label: string;
  run: () => void;
}

export interface Toast {
  id: number;
  message: string;
  /** Up to three, the last one at the far right; a Done toast can ask one question next to Undo. */
  actions: ToastAction[];
}

export interface UiState {
  column: Motivation;
  /** Columns visited before the current one, most recent last; the back button walks it. */
  columnHistory: Motivation[];
  /** A request for the board to animate to a column, made by the back button. */
  jump: { column: Motivation; nonce: number } | null;
  sheetCardId: string | null;
  quickAdd: { open: boolean; prefill: QuickAddPrefill | null };
  toast: Toast | null;
  introSeen: boolean;
  /** The board just changed from the menu or a key: the board slides in from that side. */
  boardSlide: { dir: 1 | -1; nonce: number } | null;
  slideBoard(dir: 1 | -1): void;
  /** The phone's boards menu over the bottom bar; the back button closes it first. */
  boardMenu: boolean;
  setBoardMenu(open: boolean): void;
  setColumn(column: number, record?: boolean): void;
  popColumnHistory(): Motivation | null;
  jumpTo(column: Motivation): void;
  openSheet(id: string): void;
  closeSheet(): void;
  openQuickAdd(prefill?: QuickAddPrefill): void;
  closeQuickAdd(): void;
  showToast(message: string, action?: ToastAction | ToastAction[], durationMs?: number): void;
  hideToast(id?: number): void;
  dismissIntro(): void;
}

function readColumn(): Motivation {
  try {
    const raw = sessionStorage.getItem(COLUMN_KEY);
    const v = raw === null ? Number.NaN : Number(raw);
    if (Number.isInteger(v) && v >= 0 && v < MAX_COLUMNS) return v as Motivation;
  } catch {
    // no session storage
  }
  return 1;
}

function readIntroSeen(): boolean {
  try {
    return localStorage.getItem(INTRO_KEY) === '1';
  } catch {
    return true;
  }
}

let toastSeq = 0;
let jumpSeq = 0;
let toastTimer: ReturnType<typeof setTimeout> | null = null;

export const useUi = create<UiState>((set, get) => ({
  column: readColumn(),
  columnHistory: [],
  jump: null,
  sheetCardId: null,
  quickAdd: { open: false, prefill: null },
  toast: null,
  introSeen: readIntroSeen(),
  boardSlide: null,
  slideBoard(dir) {
    set({ boardSlide: { dir, nonce: ++jumpSeq } });
  },
  boardMenu: false,
  setBoardMenu(open) {
    set({ boardMenu: open });
  },

  setColumn(column, record = true) {
    const c = Math.max(0, Math.min(MAX_COLUMNS - 1, Math.round(column))) as Motivation;
    const { column: current, columnHistory } = get();
    if (c === current) return;
    try {
      sessionStorage.setItem(COLUMN_KEY, String(c));
    } catch {
      // no session storage
    }
    const history = record && columnHistory[columnHistory.length - 1] !== current ? [...columnHistory, current].slice(-HISTORY_MAX) : columnHistory;
    set({ column: c, columnHistory: history });
  },
  popColumnHistory() {
    const { columnHistory } = get();
    if (columnHistory.length === 0) return null;
    const previous = columnHistory[columnHistory.length - 1] as Motivation;
    set({ columnHistory: columnHistory.slice(0, -1) });
    return previous;
  },
  jumpTo(column) {
    set({ jump: { column, nonce: ++jumpSeq } });
  },
  openSheet(id) {
    set({ sheetCardId: id });
  },
  closeSheet() {
    set({ sheetCardId: null });
  },
  openQuickAdd(prefill) {
    set({ quickAdd: { open: true, prefill: prefill ?? null } });
  },
  closeQuickAdd() {
    set({ quickAdd: { open: false, prefill: null } });
  },
  showToast(message, action, durationMs = 5000) {
    const id = ++toastSeq;
    if (toastTimer) clearTimeout(toastTimer);
    const actions = action === undefined ? [] : Array.isArray(action) ? action : [action];
    set({ toast: { id, message, actions } });
    toastTimer = setTimeout(() => get().hideToast(id), durationMs);
  },
  hideToast(id) {
    const current = get().toast;
    if (current && (id === undefined || current.id === id)) set({ toast: null });
  },
  dismissIntro() {
    try {
      localStorage.setItem(INTRO_KEY, '1');
    } catch {
      // storage unavailable; the note returns next time
    }
    set({ introSeen: true });
  },
}));
